import { useAuth } from '../../hooks/useAuth';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import {
    Users,
    Dumbbell,
    TrendingUp,
    Calendar,
    MessageSquare,
    ArrowRight,
    DollarSign,
    Plus,
    AlertCircle,
    CheckCircle,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { formatCurrency } from '@shared/utils';

export function DashboardPage() {
    const { profile, tenant } = useAuth();

    // Fetch dashboard stats
    const { data: stats } = useQuery({
        queryKey: ['dashboard-stats', tenant?.id],
        queryFn: async () => {
            if (!tenant?.id) return null;

            const startOfMonth = new Date();
            startOfMonth.setDate(1);
            startOfMonth.setHours(0, 0, 0, 0);

            const [studentsResult, workoutsResult, logsResult, revenueResult, overdueResult] = await Promise.all([
                supabase
                    .from('student_pairings')
                    .select('id', { count: 'exact' })
                    .eq('tenant_id', tenant.id)
                    .eq('status', 'active'),
                supabase
                    .from('workouts')
                    .select('id', { count: 'exact' })
                    .eq('tenant_id', tenant.id)
                    .eq('is_active', true),
                supabase
                    .from('workout_logs')
                    .select('id', { count: 'exact' })
                    .eq('tenant_id', tenant.id)
                    .gte('created_at', new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString()),
                supabase
                    .rpc('get_tenant_revenue', {
                        p_tenant_id: tenant.id,
                        p_start_date: startOfMonth.toISOString()
                    }),
                supabase
                    .from('invoices')
                    .select('id', { count: 'exact' })
                    .eq('tenant_id', tenant.id)
                    .eq('status', 'open')
                    .lt('due_date', new Date().toISOString())
            ]);

            return {
                students: studentsResult.count || 0,
                workouts: workoutsResult.count || 0,
                logsThisWeek: logsResult.count || 0,
                monthlyRevenue: revenueResult.data || 0,
                overdueInvoices: overdueResult.count || 0
            };
        },
        enabled: !!tenant?.id,
    });

    // Fetch inactive athletes (no log in 7 days)
    const { data: inactiveAthletes } = useQuery({
        queryKey: ['inactive-athletes', tenant?.id],
        queryFn: async () => {
            if (!tenant?.id) return [];

            const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();

            // First, get all active athlete IDs
            const { data: pairings } = await supabase
                .from('student_pairings')
                .select(`
                    student_id,
                    student:student_id (full_name, avatar_url)
                `)
                .eq('tenant_id', tenant.id)
                .eq('status', 'active');

            if (!pairings || pairings.length === 0) return [];

            const athleteIds = pairings.map(p => p.student_id);

            // Then, get IDs of athletes who DID train in the last 7 days
            const { data: recentLogs } = await supabase
                .from('workout_logs')
                .select('student_id')
                .eq('tenant_id', tenant.id)
                .gte('created_at', sevenDaysAgo);

            const activeIds = new Set(recentLogs?.map(l => l.student_id) || []);

            // Inactive = Total - Active
            return pairings
                .filter(p => !activeIds.has(p.student_id))
                .map(p => ({
                    id: p.student_id,
                    full_name: (p.student as any)?.full_name,
                    avatar_url: (p.student as any)?.avatar_url,
                }));
        },
        enabled: !!tenant?.id,
    });

    // Fetch recent logs
    const { data: recentLogs } = useQuery({
        queryKey: ['recent-logs', tenant?.id],
        queryFn: async () => {
            if (!tenant?.id) return [];
            const { data } = await supabase
                .from('workout_logs')
                .select(`
                    id,
                    started_at,
                    completed_at,
                    student:student_id (full_name),
                    workout:workout_id (name)
                `)
                .eq('tenant_id', tenant.id)
                .order('started_at', { ascending: false })
                .limit(5);
            return data || [];
        },
        enabled: !!tenant?.id,
    });

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="section-title">
                        E aí, {profile?.full_name?.split(' ')[0]}! 💪
                    </h1>
                    <p className="section-subtitle">
                        Vamos ver como está indo seu negócio! 🚀
                    </p>
                </div>
                <Link to="/workouts/new" className="btn-primary">
                    <Plus className="w-4 h-4 mr-2" />
                    Novo Treino
                </Link>
            </div>

            {/* Stats cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="card">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                Alunos Ativos
                            </p>
                            <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                                {stats?.students || 0}
                            </p>
                        </div>
                        <div className="w-12 h-12 rounded-xl bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
                            <Users className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                        </div>
                    </div>
                    <Link
                        to="/students"
                        className="mt-4 flex items-center text-sm text-primary-600 hover:text-primary-700"
                    >
                        Ver alunos
                        <ArrowRight className="w-4 h-4 ml-1" />
                    </Link>
                </div>

                <div className="card">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                Treinos Criados
                            </p>
                            <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                                {stats?.workouts || 0}
                            </p>
                        </div>
                        <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                            <Dumbbell className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                        </div>
                    </div>
                    <Link
                        to="/workouts"
                        className="mt-4 flex items-center text-sm text-primary-600 hover:text-primary-700"
                    >
                        Ver treinos
                        <ArrowRight className="w-4 h-4 ml-1" />
                    </Link>
                </div>

                <div className="card">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                Faturamento (Mês)
                            </p>
                            <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                                {formatCurrency(stats?.monthlyRevenue || 0)}
                            </p>
                        </div>
                        <div className="w-12 h-12 rounded-xl bg-emerald-100 dark:bg-emerald-900 flex items-center justify-center">
                            <DollarSign className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                        </div>
                    </div>
                    <Link
                        to="/financial"
                        className="mt-4 flex items-center text-sm text-primary-600 hover:text-primary-700"
                    >
                        Ver financeiro
                        <ArrowRight className="w-4 h-4 ml-1" />
                    </Link>
                </div>

                <div className="card">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                Inadimplência
                            </p>
                            <p className="text-2xl font-bold text-red-600 mt-1">
                                {stats?.overdueInvoices || 0}
                            </p>
                        </div>
                        <div className="w-12 h-12 rounded-xl bg-red-100 dark:bg-red-900 flex items-center justify-center">
                            <Calendar className="w-6 h-6 text-red-600 dark:text-red-400" />
                        </div>
                    </div>
                    <p className="mt-4 text-sm text-gray-500">
                        Faturas vencidas
                    </p>
                </div>
            </div>

            {/* Main content */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Recent activity */}
                <div className="lg:col-span-2 card">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                            Atividade Recente
                        </h2>
                        <Link to="/students" className="text-sm text-primary-600 hover:text-primary-700">
                            Ver tudo
                        </Link>
                    </div>

                    {recentLogs && recentLogs.length > 0 ? (
                        <div className="space-y-3">
                            {recentLogs.map((log: any) => (
                                <div
                                    key={log.id}
                                    className="flex items-center gap-4 p-3 rounded-lg bg-gray-50 dark:bg-gray-800"
                                >
                                    <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
                                        <Dumbbell className="w-5 h-5 text-primary-600" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                            {log.student?.full_name || 'Aluno'}
                                        </p>
                                        <p className="text-xs text-gray-500 truncate">
                                            {log.workout?.name || 'Treino'}
                                        </p>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-xs text-gray-500">
                                            {new Date(log.started_at).toLocaleDateString('pt-BR')}
                                        </p>
                                        <span
                                            className={`badge ${log.completed_at ? 'badge-success' : 'badge-warning'
                                                }`}
                                        >
                                            {log.completed_at ? 'Completo' : 'Em andamento'}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-8">
                            <p className="text-sm text-gray-500">Nenhuma atividade recente encontrada.</p>
                        </div>
                    )}
                </div>

                {/* Inactive Athletes Sidebar */}
                <div className="card">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                            <AlertCircle className="w-5 h-5 text-amber-500" />
                            Atletas Inativos
                        </h2>
                        <span className="badge badge-warning">
                            +7 dias
                        </span>
                    </div>

                    {inactiveAthletes && inactiveAthletes.length > 0 ? (
                        <div className="space-y-3">
                            {inactiveAthletes.slice(0, 5).map((athlete) => (
                                <Link
                                    key={athlete.id}
                                    to={`/students/${athlete.id}`}
                                    className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors group"
                                >
                                    <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 font-bold overflow-hidden shadow-sm">
                                        {athlete.avatar_url ? (
                                            <img src={athlete.avatar_url} alt="" className="w-full h-full object-cover" />
                                        ) : (
                                            athlete.full_name?.charAt(0).toUpperCase()
                                        )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate group-hover:text-primary-600">
                                            {athlete.full_name}
                                        </p>
                                    </div>
                                    <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-primary-400" />
                                </Link>
                            ))}
                            {inactiveAthletes.length > 5 && (
                                <p className="text-xs text-center text-gray-500 pt-2">
                                    + {inactiveAthletes.length - 5} outros atletas inativos
                                </p>
                            )}
                        </div>
                    ) : (
                        <div className="text-center py-6">
                            <CheckCircle className="w-10 h-10 text-green-500/20 mx-auto mb-2" />
                            <p className="text-sm text-gray-500">Maravilha! Todos treinando! 🏆</p>
                        </div>
                    )}
                    <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
                        <p className="text-xs text-gray-400">
                            Atletas que não registram treinos há mais de uma semana aparecem aqui.
                        </p>
                    </div>
                </div>

                {/* Quick actions */}
                <div className="card">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                        Ações Rápidas
                    </h2>
                    <div className="space-y-2">
                        <Link
                            to="/students"
                            className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                        >
                            <div className="w-10 h-10 rounded-lg bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
                                <Users className="w-5 h-5 text-primary-600" />
                            </div>
                            <div>
                                <p className="font-medium text-gray-900 dark:text-white">
                                    Convidar Aluno
                                </p>
                                <p className="text-xs text-gray-500">
                                    Adicione um novo aluno
                                </p>
                            </div>
                        </Link>
                        <Link
                            to="/workouts/new"
                            className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                        >
                            <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                                <Dumbbell className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                                <p className="font-medium text-gray-900 dark:text-white">
                                    Criar Treino
                                </p>
                                <p className="text-xs text-gray-500">
                                    Monte um novo treino
                                </p>
                            </div>
                        </Link>
                        <Link
                            to="/exercises"
                            className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                        >
                            <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900 flex items-center justify-center">
                                <TrendingUp className="w-5 h-5 text-purple-600" />
                            </div>
                            <div>
                                <p className="font-medium text-gray-900 dark:text-white">
                                    Biblioteca
                                </p>
                                <p className="text-xs text-gray-500">
                                    Gerencie exercícios
                                </p>
                            </div>
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
}
